/**
 * Measurement module — service layer.
 *
 * Bridges UI to the repository and to Cloud Functions.
 *
 * Two paths for write operations:
 *  - Mutations on existing MCs (save schedule, transition status) go
 *    directly to Firestore via the repository.
 *  - Creation from a quote and the customer token flows go through Cloud
 *    Functions so the MC number, deposit gate, and slot validation are
 *    server-authoritative.
 *
 * The localStorage offline mirror — the largest in the codebase — was
 * deleted in the dual-path retirement (Phase 2b); local dev runs against
 * the emulator (`npm run dev:emu`).
 */

import { httpsCallable } from 'firebase/functions';
import { repositories } from '../../../data/repositories';
import { functions } from '../../../lib/firebase';
import type {
  EntityId,
  MeasurementConfirmation,
  MeasurementStatus,
  MeasurementFile,
} from '../../../types';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../lib/firebase';
import { resolveUploadContentType } from '../../../utils/uploadContentType';
import { byCreatedDesc } from '../../../utils/sort';
import { isWorkingDay, parseDateKey } from '../../../../shared/scheduling';
import { canAdvanceMeasurement } from './measurementTransitions';

interface CreateMcResult {
  id: EntityId;
  mcNumber: string;
  projectId: EntityId;
  reused: boolean;
  /** Customer scheduling token, minted server-side. Used by the post-payment
   * redirect so the customer flow never needs a second unauth callable. */
  token: string;
  tokenExpiresAt: string;
}

const createFromQuoteCallable = httpsCallable<
  { quoteId: string; forceRecreate?: boolean },
  CreateMcResult
>(functions, 'createMeasurementFromQuote');

interface BookSlotResult {
  ok: true;
  mcId: string;
  scheduledDate: string;
}

const bookSlotCallable = httpsCallable<{ token: string; scheduledDate: string }, BookSlotResult>(
  functions,
  'bookMeasurementSlot',
);

const getCapacitiesCallable = httpsCallable<
  { dateKeys: string[] },
  Record<string, { booked: number }>
>(functions, 'getMeasurementCapacities');

const requestCallbackCallable = httpsCallable<
  { token: string; note?: string },
  { ok: true; mcId: string }
>(functions, 'requestMeasurementCallback');

export const createMeasurementFromQuote = async (
  quoteId: string,
  opts: { forceRecreate?: boolean } = {},
): Promise<CreateMcResult> => {
  const result = await createFromQuoteCallable({ quoteId, forceRecreate: opts.forceRecreate });
  return result.data;
};

export const fetchMeasurement = async (id: EntityId): Promise<MeasurementConfirmation | null> => {
  return repositories.measurements.findById(id);
};

export const listMeasurementsByOrder = async (
  projectId: EntityId,
): Promise<MeasurementConfirmation[]> => {
  const mcs = await repositories.measurements.findByOrder(projectId);
  return mcs.sort(byCreatedDesc);
};

/**
 * List all MCs the company has, optionally filtered to a single measurer.
 * Used by the global Schedule page.
 *
 * For now this fetches the full collection. Promote to a paginated query
 * when measurement volume justifies it.
 */
export const listScheduledMeasurements = async (
  opts: { measurer?: string } = {},
): Promise<MeasurementConfirmation[]> => {
  const all = opts.measurer
    ? await repositories.measurements.findByMeasurer(opts.measurer)
    : await repositories.measurements.list();
  return all
    .filter((m) => !!m.scheduledDate)
    .sort((a, b) => {
      const byDate = (a.scheduledDate ?? '').localeCompare(b.scheduledDate ?? '');
      return byDate !== 0 ? byDate : a.mcNumber.localeCompare(b.mcNumber);
    });
};

/**
 * MCs with an open customer callback request — the customer asked staff to
 * call them and arrange the visit, and it isn't scheduled yet. Powers the
 * staff dashboard section and the measurements-list badge.
 *
 * Treats a request as "open" while `scheduledDate` is unset; once staff book
 * a slot the MC drops off this list (the flag itself is left as a record).
 * Fetches the full collection (same pattern as `listScheduledMeasurements`) —
 * promote to a query when measurement volume justifies it.
 */
export const listCallbackRequests = async (): Promise<MeasurementConfirmation[]> => {
  const all = await repositories.measurements.list();
  return all
    .filter((m) => m.customerCallbackRequested === true && !m.scheduledDate)
    .sort((a, b) =>
      (b.customerCallbackRequestedAt ?? '').localeCompare(a.customerCallbackRequestedAt ?? ''),
    );
};

const persist = async (mc: MeasurementConfirmation): Promise<void> => {
  await repositories.measurements.save(mc);
};

export const uploadMeasurementFile = async (
  mcId: EntityId,
  file: File,
  actor: string,
  opts: { openingId?: string; kind?: 'SITE' } = {},
): Promise<MeasurementConfirmation> => {
  const mc = await fetchMeasurement(mcId);
  if (!mc) throw new Error(`Measurement ${mcId} not found.`);

  const fileId = crypto.randomUUID();
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');

  // Resolve a contentType the storage.rules allowlist accepts — a File with a
  // blank `.type` would otherwise upload as octet-stream and be rejected.
  const contentType = resolveUploadContentType(file);
  if (!contentType) {
    throw new Error(
      'Unsupported file type. Upload a PDF or an image (PNG, JPG, WEBP, HEIC, or GIF).',
    );
  }

  const storagePath = `measurements/${mcId}/${fileId}_${safeName}`;
  const storageRef = ref(storage, storagePath);
  const snapshot = await uploadBytes(storageRef, file, { contentType });
  const url = await getDownloadURL(snapshot.ref);

  const now = new Date().toISOString();
  const newFile: MeasurementFile = {
    id: fileId,
    url,
    name: file.name,
    type: contentType,
    uploadedAt: now,
    uploadedBy: actor,
    storagePath,
    ...(opts.openingId ? { openingId: opts.openingId } : {}),
    ...(opts.kind ? { kind: opts.kind } : {}),
  };

  const files = [...(mc.files || []), newFile];

  const updated: MeasurementConfirmation = {
    ...mc,
    files,
    updatedAt: now,
    updatedBy: actor,
  };
  await persist(updated);
  return updated;
};

export const deleteMeasurementFile = async (
  mcId: EntityId,
  fileId: EntityId,
  actor: string,
): Promise<MeasurementConfirmation> => {
  const mc = await fetchMeasurement(mcId);
  if (!mc) throw new Error(`Measurement ${mcId} not found.`);

  // Real delete, not the historical half-delete (B10): remove the Storage blob
  // when we know its path, then drop the Firestore entry. Blob removal is
  // best-effort — a missing/already-gone object must not strand the entry.
  // Legacy entries (pre-2026-08) carry no storagePath; for those only the
  // entry is removed, same as before.
  const target = (mc.files || []).find((f) => f.id === fileId);
  if (target?.storagePath) {
    try {
      await deleteObject(ref(storage, target.storagePath));
    } catch (e) {
      console.warn('deleteMeasurementFile: blob removal failed (entry still removed)', e);
    }
  }

  const files = (mc.files || []).filter((f) => f.id !== fileId);
  const updated: MeasurementConfirmation = {
    ...mc,
    files,
    updatedAt: new Date().toISOString(),
    updatedBy: actor,
  };
  await persist(updated);
  return updated;
};

export interface ScheduleMeasurementInput {
  /** YYYY-MM-DD */
  scheduledDate: string;
  assignedMeasurer?: string;
}

export const scheduleMeasurement = async (
  mcId: EntityId,
  input: ScheduleMeasurementInput,
  actor: string,
): Promise<MeasurementConfirmation> => {
  const mc = await fetchMeasurement(mcId);
  if (!mc) throw new Error(`Measurement ${mcId} not found.`);
  if (mc.status === 'CANCELLED' || mc.status === 'APPROVED') {
    throw new Error(`Cannot schedule measurement in status ${mc.status}.`);
  }
  if (!isWorkingDay(parseDateKey(input.scheduledDate))) {
    throw new Error(`${input.scheduledDate} is not a working day.`);
  }

  const now = new Date().toISOString();
  const updated: MeasurementConfirmation = {
    ...mc,
    scheduledDate: input.scheduledDate,
    scheduledByCustomer: false,
    scheduledAt: now,
    assignedMeasurer: input.assignedMeasurer ?? mc.assignedMeasurer,
    status: mc.status === 'PENDING_SCHEDULING' ? 'SCHEDULED' : mc.status,
    updatedAt: now,
    updatedBy: actor,
  };
  await persist(updated);
  return updated;
};

/**
 * Reassign (or assign) the measurer on an MC. Admin-only in the UI; the
 * Firestore rule lets an ADMIN update any MC. `measurerEmail` is normalised
 * to lowercase to match the `assignedMeasurer == emailLower()` rule predicate
 * a measurer relies on to open their own MCs.
 */
export const assignMeasurer = async (
  mcId: EntityId,
  measurerEmail: string,
  actor: string,
): Promise<MeasurementConfirmation> => {
  const normalized = measurerEmail.trim().toLowerCase();
  if (!normalized) throw new Error('A measurer email is required.');
  const mc = await fetchMeasurement(mcId);
  if (!mc) throw new Error(`Measurement ${mcId} not found.`);
  const now = new Date().toISOString();
  const updated: MeasurementConfirmation = {
    ...mc,
    assignedMeasurer: normalized,
    updatedAt: now,
    updatedBy: actor,
  };
  await persist(updated);
  return updated;
};

/**
 * Issues a scheduling token for the customer-facing link. Idempotent: if a
 * non-expired token already exists, returns the same one. Reset (rotate) is
 * by passing `regen=true`. Expired tokens are automatically replaced.
 */
export const generateSchedulingToken = async (
  mcId: EntityId,
  _actor: string,
  opts: { regen?: boolean } = {},
): Promise<{ token: string; reused: boolean }> => {
  const callable = httpsCallable<
    { mcId: string; regen?: boolean },
    { token: string; reused: boolean }
  >(functions, 'generateSchedulingToken');
  const res = await callable({ mcId, regen: opts.regen });
  return res.data;
};

/**
 * Look up an MC by its public scheduling token. Used by the customer-facing
 * `/schedule/:token` page. Queries via a Cloud Function (the Admin SDK) —
 * client-side token queries are blocked by Firestore rules.
 */
const isTokenExpired = (mc: MeasurementConfirmation): boolean => {
  const exp = mc.customerSchedulingTokenExpiresAt;
  if (!exp) return false; // Pre-expiry tokens (legacy) are still honoured here.
  return new Date(exp).getTime() <= Date.now();
};

export const fetchMeasurementByToken = async (
  token: string,
): Promise<MeasurementConfirmation | null> => {
  if (!token) return null;
  let mc: MeasurementConfirmation | null;
  const getMeasurement = httpsCallable<{ token: string }, MeasurementConfirmation | null>(
    functions,
    'getMeasurementByToken',
  );
  try {
    const res = await getMeasurement({ token });
    mc = res.data;
  } catch (e) {
    console.error('Failed to fetch measurement by token via function', e);
    mc = null;
  }
  if (mc && isTokenExpired(mc)) return null;
  return mc;
};

/**
 * Customer-side booking via the public link. Validates the slot, marks the
 * token used, advances status to SCHEDULED. Idempotent: re-booking the same
 * slot is a no-op; booking a different slot replaces the previous one as
 * long as the token is still valid.
 */
export const bookSlotByToken = async (
  token: string,
  scheduledDate: string,
): Promise<MeasurementConfirmation> => {
  // Customers are unauthenticated, so the write goes through the Cloud Function
  // (which validates the day + capacity). Re-fetch the MC after the call to
  // return the updated state to the caller.
  await bookSlotCallable({ token, scheduledDate });
  const refreshed = await fetchMeasurementByToken(token);
  if (!refreshed) throw new Error('Booking succeeded but the measurement could not be reloaded.');
  return refreshed;
};

/**
 * Customer-side callback request via the public link. The customer used the
 * scheduling page but none of the offered slots worked, so they ask staff to
 * call and arrange the visit manually. Sets a flag on the MC — NOT a status
 * change — and (in production) fires a notification to the sales rep.
 *
 * Goes through the `requestMeasurementCallback` Cloud Function (the customer
 * is unauthenticated, so direct writes are blocked by Firestore rules).
 */
export const requestMeasurementCallback = async (
  token: string,
  note?: string,
): Promise<MeasurementConfirmation> => {
  await requestCallbackCallable({ token, note });
  const refreshed = await fetchMeasurementByToken(token);
  if (!refreshed) {
    throw new Error('Your request was saved but the measurement could not be reloaded.');
  }
  return refreshed;
};

export const transitionStatus = async (
  mcId: EntityId,
  toStatus: MeasurementStatus,
  actor: string,
  meta?: { approvalNotes?: string; cancelledReason?: string },
): Promise<MeasurementConfirmation> => {
  const mc = await fetchMeasurement(mcId);
  if (!mc) throw new Error(`Measurement ${mcId} not found.`);
  // Pre-validate against the rules mirror so the caller gets a readable error
  // instead of a raw permission-denied. ADMIN approval legitimately breaks the
  // non-admin machine (the rules' exempt branch), so APPROVED is passed through
  // — the rule, not this mirror, is the enforcement.
  if (toStatus !== 'APPROVED' && !canAdvanceMeasurement(mc.status, toStatus)) {
    throw new Error(`Cannot move measurement from ${mc.status} to ${toStatus}.`);
  }
  const now = new Date().toISOString();
  const patch: Partial<MeasurementConfirmation> = {
    status: toStatus,
    updatedAt: now,
    updatedBy: actor,
  };
  if (toStatus === 'APPROVED') {
    patch.approvedAt = now;
    patch.approvedBy = actor;
    patch.approvalNotes = meta?.approvalNotes;
  } else if (toStatus === 'CANCELLED') {
    patch.cancelledAt = now;
    patch.cancelledReason = meta?.cancelledReason;
  } else if (toStatus === 'COMPLETED') {
    patch.completedAt = mc.completedAt ?? now;
  }
  const updated: MeasurementConfirmation = { ...mc, ...patch } as MeasurementConfirmation;
  await persist(updated);
  return updated;
};

export const fetchMeasurementCapacities = async (
  dateKeys: string[],
): Promise<Record<string, { booked: number }>> => {
  if (dateKeys.length === 0) return {};
  // Chunk the request in case there are more than 30 dates (to avoid firestore query limits)
  const chunks = [];
  for (let i = 0; i < dateKeys.length; i += 30) {
    chunks.push(dateKeys.slice(i, i + 30));
  }

  const results = await Promise.all(
    chunks.map((chunk) => getCapacitiesCallable({ dateKeys: chunk })),
  );

  let finalResult: Record<string, { booked: number }> = {};
  for (const res of results) {
    finalResult = { ...finalResult, ...res.data };
  }

  return finalResult;
};
